import { RelationshipDiagram } from './relationship-diagram.model';
import * as d3 from 'd3';

/** Per-diagram zoom state so controls and redraws stay consistent. */
const diagramZoomById: Map<string, { svg: any; zoom: any; contentW: number; contentH: number }> = new Map();

/** Margin as a fraction of viewport width/height (symmetric). */
const FIT_MARGIN_RATIO = 0.06;

function computeFitTransform(viewW: number, viewH: number, contentW: number, contentH: number): d3.ZoomTransform {
    const mx = viewW * FIT_MARGIN_RATIO;
    const my = viewH * FIT_MARGIN_RATIO;
    const innerW = Math.max(viewW - 2 * mx, 1);
    const innerH = Math.max(viewH - 2 * my, 1);
    const cw = Math.max(contentW, 1);
    const ch = Math.max(contentH, 1);
    const k = Math.min(innerW / cw, innerH / ch);
    const tx = (viewW - k * cw) / 2;
    const ty = (viewH - k * ch) / 2;
    return d3.zoomIdentity.translate(tx, ty).scale(k);
}

function applyFitToView(
    svgSel: d3.Selection<d3.BaseType, {}, null, undefined>,
    zoom: d3.ZoomBehavior<SVGSVGElement, any>,
    contentW: number,
    contentH: number,
    animated: boolean
): void {
    const el = svgSel.node() as SVGSVGElement;
    let vw = el.clientWidth;
    let vh = el.clientHeight;
    if (vw < 2 || vh < 2) {
        const vb = el.viewBox && el.viewBox.baseVal;
        if (vb && vb.width > 0 && vb.height > 0) {
            vw = vb.width;
            vh = vb.height;
        } else {
            vw = Math.max(contentW, 1);
            vh = Math.max(contentH, 1);
        }
    }
    svgSel.attr('viewBox', '0 0 ' + vw + ' ' + vh);
    const t = computeFitTransform(vw, vh, contentW, contentH);
    const target = animated ? svgSel.transition().duration(220) : svgSel;
    (target as any).call(zoom.transform as any, t);
}

export class StructureDiagramLayoutManager {

    public static draw(center: RelationshipDiagram, relationshipDiagram: string) {
        const svgSel = d3.select('#' + relationshipDiagram);
        svgSel.on('.zoom', null);
        svgSel.selectAll('*').remove();

        const mainArea = new DrawableArea(center);
        const w = mainArea.getWidth();
        const h = mainArea.getHeight();

        svgSel
            .attr('width', '100%')
            .attr('height', '100%')
            .attr('preserveAspectRatio', 'xMidYMid meet');

        const content = svgSel.append('g').attr('class', 'relationship-diagram-content');
        SvgContainer.svg = content;

        mainArea.draw(0, 0);

        const zoom = d3.zoom<SVGSVGElement, any>()
            .scaleExtent([0.08, 6])
            .on('zoom', () => {
                const t = (d3 as any).event.transform;
                content.attr('transform', t);
            });

        svgSel.call(zoom as any);
        diagramZoomById.set(relationshipDiagram, { svg: svgSel, zoom, contentW: w, contentH: h });

        let attempts = 0;
        const tryFit = () => {
            attempts++;
            const el = svgSel.node() as SVGSVGElement;
            const cw = el.clientWidth;
            const ch = el.clientHeight;
            if ((cw < 2 || ch < 2) && attempts < 40) {
                requestAnimationFrame(tryFit);
                return;
            }
            applyFitToView(svgSel, zoom, w, h, false);
        };
        requestAnimationFrame(tryFit);
    }

    public static zoomIn(relationshipDiagram: string): void {
        const state = diagramZoomById.get(relationshipDiagram);
        if (state) {
            state.svg.transition().duration(180).call(state.zoom.scaleBy as any, 1.25);
        }
    }

    public static zoomOut(relationshipDiagram: string): void {
        const state = diagramZoomById.get(relationshipDiagram);
        if (state) {
            state.svg.transition().duration(180).call(state.zoom.scaleBy as any, 0.8);
        }
    }

    public static resetZoom(relationshipDiagram: string): void {
        const state = diagramZoomById.get(relationshipDiagram);
        if (state) {
            applyFitToView(state.svg, state.zoom, state.contentW, state.contentH, true);
        }
    }

}

class SvgContainer {
    public static svg: any;
    public static nodepadding: number = 10;
    public static layerpadding: number = 20;
}

class DrawableArea {

    private x: number = 0;
    private y: number = 0;
    private width: number;
    private height: number;
    private rightside_positioning: number = 0;
    private leftside_positioning: number = 0;
    private centerNode: NodeLayout;
    private childAreas: DrawableArea[] = [];
    private parentAreas: DrawableArea[] = [];

    constructor(nodeVO: RelationshipDiagram) {
        this.calculateNodeAreaForCenterNode(nodeVO);
        this.calculateTheHightAndWidthOfCenterNode();
        this.calculateTheAreaOfChildNodes(nodeVO);
        this.calculateTheAreaOfParentNodes(nodeVO);
        this.calculatePositionArea(this.childAreas, this.hasChildren, false);
        this.calculatePositionArea(this.parentAreas, this.hasParents, true);
    }

    private calculatePositionArea(drawableAreas: DrawableArea[], hasHierarchy: any, calculateRelativeConnectedNodes: boolean) {
        if (drawableAreas.length == 1) {
            let singleParent = drawableAreas[0]
            this.positionSingleArea(singleParent, calculateRelativeConnectedNodes);
        } else if (drawableAreas.length > 1) {
            this.leftside_positioning = this.centerNode.getX() + (this.centerNode.getWidth() / 2);
            this.rightside_positioning = this.leftside_positioning;

            // position each parentarea in the area depending it has parents shift left or right
            // move upwards downwards to prevent line crossings
            let orphans: DrawableArea[] = [];
            let families: DrawableArea[] = [];

            drawableAreas.forEach(drawableArea => {
                if (hasHierarchy(drawableArea)) {
                    families.push(drawableArea);
                } else {
                    orphans.push(drawableArea);
                }
            })
            this.positionOrphanAreas(orphans, calculateRelativeConnectedNodes);
            this.positionFamilyAreas(families, calculateRelativeConnectedNodes);
        }
    }

    private calculateNodeAreaForCenterNode(nodeVO: RelationshipDiagram) {
        this.centerNode = NodeLayout.createNodeLayout(nodeVO)
    }

    private calculateTheHightAndWidthOfCenterNode() {
        this.width = this.centerNode.getWidth();
        this.height = this.centerNode.getHeight();
    }

    private calculateTheAreaOfChildNodes(nodeVO: RelationshipDiagram) {
        if (nodeVO.children != null) {
            nodeVO.children.forEach(element => {
                this.childAreas.push(new DrawableArea(element));
            });
        }
    }

    private calculateTheAreaOfParentNodes(nodeVO: RelationshipDiagram) {
        if (nodeVO.parents != null) {
            nodeVO.parents.forEach(element => {
                this.parentAreas.push(new DrawableArea(element));
            });
        }
    }

    //TODO: This is copied from GWT code. This can be refactored to improve the code. 
    private positionOrphanAreas(areas: DrawableArea[], up: boolean) {
        let leftColumn = false;

        let increment_leftside = 0;
        let decrement_rightside = 0;

        let rowidx = -1;
        for (let i = 0; i < areas.length; i++) {
            if (i % 2 == 0) {
                rowidx = rowidx + 1;
            }
            let area = areas[i];
            // determine y pos
            let newy = 0;
            if (!up) {
                newy = this.centerNode.getY() + this.centerNode.getHeight()
                    + SvgContainer.layerpadding + (this.centerNode.getHeight() * rowidx);
                // increase height as needed
                if (newy + area.getHeight() > this.height) {
                    this.incrementHeigth(newy + area.getHeight() - this.height, false);
                }
            } else {
                newy = this.centerNode.getY() - SvgContainer.layerpadding - (this.centerNode.getHeight() * rowidx) - area.getHeight();
                // increase height as needed
                if (newy < 0) {
                    this.incrementHeigth(-newy, true);
                    newy = 0;
                }
            }

            if (!leftColumn) {
                // check on fit
                if (this.leftside_positioning + area.getWidth() > this.width) {
                    this.incrementWidth(this.leftside_positioning + area.getWidth() - this.width, false);
                }
                area.setPosition(this.leftside_positioning, newy);
                if (area.getWidth() > increment_leftside) {
                    increment_leftside = area.getWidth();
                }
            } else {
                let newx = this.rightside_positioning - area.getWidth();
                if (newx < 0) {
                    this.incrementWidth(-newx, true);
                    newx = 0;
                }
                area.setPosition(newx, newy);
                if (area.getWidth() > decrement_rightside) {
                    decrement_rightside = area.getWidth();
                }
            }

            leftColumn = !leftColumn;
        }

        this.leftside_positioning = this.leftside_positioning + increment_leftside;
        this.rightside_positioning = this.rightside_positioning - decrement_rightside;
    }

    //TODO: This is copied from GWT code. This can be refactored to improve the code. 
    private positionFamilyAreas(areas: DrawableArea[], up: boolean) {
        let leftColumn = false;
        for (let i = 0; i < areas.length; i++) {
            let family = areas[i];
            let newy = 0;
            if (!up) {
                newy = this.centerNode.getY() + this.centerNode.getHeight() + SvgContainer.layerpadding;
                if (newy + family.getHeight() > this.height) {
                    this.incrementHeigth(newy + family.getHeight() - this.height, up);
                }
            } else {
                newy = this.centerNode.getY() - SvgContainer.layerpadding - family.getHeight();
                if (newy < 0) {
                    this.incrementHeigth(0 - newy, true);
                    newy = 0;
                }
            }

            if (!leftColumn) {
                // check on fit
                if (this.leftside_positioning + family.getWidth() > this.width) {
                    this.incrementWidth(this.leftside_positioning + family.getWidth() - this.width, false);
                }
                family.setPosition(this.leftside_positioning, newy);
                this.leftside_positioning = this.leftside_positioning + family.getWidth();
            } else {
                let newx = this.rightside_positioning - family.getWidth();
                if (newx < 0) {
                    this.incrementWidth(-newx, true);
                    newx = 0;
                }
                family.setPosition(newx, newy);
                this.rightside_positioning = this.rightside_positioning - family.getWidth();
            }

            leftColumn = !leftColumn;
        }
    }

    private positionSingleArea(connectedNode: DrawableArea, calculateUppersideOfCenterNode: boolean) {
        let centerNodeVerticallyMidPoint = this.centerNode.getX() + (this.centerNode.getWidth() / 2);
        let connectedNodeVerticallyMidPoint = connectedNode.centerNode.getX() + (connectedNode.centerNode.getWidth() / 2);

        let verticallyMidPointDifference = centerNodeVerticallyMidPoint - connectedNodeVerticallyMidPoint;
        if (verticallyMidPointDifference < 0) {
            this.incrementWidth(0 - verticallyMidPointDifference, true);
            verticallyMidPointDifference = 0;
        }
        if (connectedNode.getWidth() > this.width) {
            this.incrementWidth(connectedNode.getWidth() - this.width, false);
        }

        let newy = 0;
        if (!calculateUppersideOfCenterNode) {
            newy = this.centerNode.getY() + this.centerNode.getHeight() + SvgContainer.layerpadding;
            let increment = newy + connectedNode.getHeight() - this.height;
            if (increment > 0) {
                this.incrementHeigth(increment, calculateUppersideOfCenterNode);
            }
        } else {
            newy = this.centerNode.getY() - (SvgContainer.layerpadding + connectedNode.getHeight());
            if (newy < 0) {
                this.incrementHeigth(0 - newy, calculateUppersideOfCenterNode);
                newy = 0;
            }
        }

        connectedNode.setPosition(verticallyMidPointDifference, newy);
    }

    private incrementWidth(increment: number, calculateRelativeConnectedNodes: boolean) {
        // shift node and each drawable area relative position
        if (calculateRelativeConnectedNodes) {
            this.centerNode.setPosition(this.centerNode.getX() + increment, this.centerNode.getY());
            this.childAreas.forEach(child => {
                child.setPosition(child.getX() + increment, child.getY());
            })

            this.parentAreas.forEach(parent => {
                parent.setPosition(parent.getX() + increment, parent.getY());
            })

            this.leftside_positioning = this.leftside_positioning + increment;
            this.rightside_positioning = this.rightside_positioning + increment;
        }
        this.width = this.width + increment;
    }

    private incrementHeigth(increment: number, calculateRelativeConnectedNodes: boolean) {
        if (calculateRelativeConnectedNodes) {
            this.centerNode.setPosition(this.centerNode.getX(), this.centerNode.getY() + increment);

            this.childAreas.forEach(child => {
                child.setPosition(child.getX(), increment + child.getY());
            })

            this.parentAreas.forEach(parent => {
                parent.setPosition(parent.getX(), increment + parent.getY());
            })
        }
        this.height = this.height + increment;
    }

    public draw(x: number, y: number) {
        let exactx = x + this.x;
        let exacty = y + this.y;
        this.centerNode.draw(exactx, exacty);
        this.drawConnections(exactx, exacty);
        this.childAreas.forEach(child => {
            child.draw(exactx, exacty);
        })

        this.parentAreas.forEach(parent => {
            parent.draw(exactx, exacty);
        })

    }

    private hasParents(drawableArea: DrawableArea): Boolean {
        return drawableArea.centerNode.getNode().parents.length != 0;
    }

    private hasChildren(drawableArea: DrawableArea): Boolean {
        return drawableArea.centerNode.getNode().children.length != 0;
    }

    //TODO: This is copied from GWT code. This can be refactored to improve the code. 
    private drawConnections(x: number, y: number) {
        if (this.parentAreas.length == 1) {
            // draw connecting line
            let exactx = x + this.centerNode.getX() + this.centerNode.getWidth() / 2;
            let exacty1 = y + this.centerNode.getY() + SvgContainer.nodepadding;
            let area = this.parentAreas[0];
            let exacty2 = y + area.getY() + area.centerNode.getY() + area.centerNode.getHeight() - SvgContainer.nodepadding;
            this.drawLine(exactx, exacty1, exactx, exacty2);
        } else if (this.parentAreas.length > 1) {
            let exactx = x + this.centerNode.getX() + this.centerNode.getWidth() / 2;
            let exacty1 = y + this.centerNode.getY() + SvgContainer.nodepadding;
            // first part of line to crosspoint
            //                DrawableArea area = parentAreas.get(0);
            let exacty2 = exacty1 - SvgContainer.layerpadding;
            this.drawLine(exactx, exacty1, exactx, exacty2);

            let orphans: DrawableArea[] = [];
            let families: DrawableArea[] = [];

            this.parentAreas.forEach(parent => {
                if (this.hasParents(parent)) {
                    families.push(parent)
                } else {
                    orphans.push(parent);
                }
            })

            // for each to the family areas we have to draw to lines from the crossing point
            // also for the first layer of orphans
            families.forEach(area => {
                // draw to lines one from the connection point to the center of the area centernode
                // and the other one from that point to the
                let familyx = x + area.getX() + area.centerNode.getX() + (area.centerNode.getWidth() / 2);
                let familyy = y + area.getY() + area.centerNode.getY() + area.centerNode.getHeight() - SvgContainer.nodepadding;
                this.drawLine(exactx, exacty2, familyx, exacty2);
                this.drawLine(familyx, exacty2, familyx, familyy);
            })

            let count = 0, row = 0;
            let connectionx = exactx, connectiony = exacty2;

            orphans.forEach(area => {
                if ((count % 2) == 0) {
                    row++;
                    // draw line from connection point to new connection point for each new row
                    if (row > 1) {
                        let newcy = y + area.getY() + area.centerNode.getY() + (area.centerNode.getHeight() / 2);
                        this.drawLine(connectionx, connectiony, connectionx, newcy);
                        connectiony = newcy;
                    }
                }

                // draw line for first row similar to families
                if (row == 1) {
                    let familyx = x + area.getX() + area.centerNode.getX() + (area.centerNode.getWidth() / 2);
                    let familyy = y + area.getY() + area.centerNode.getY() + area.centerNode.getHeight() - SvgContainer.nodepadding;
                    this.drawLine(exactx, exacty2, familyx, exacty2);
                    this.drawLine(familyx, exacty2, familyx, familyy);
                } else {
                    // draw line from new connection point to area
                    if (x + area.getX() + area.centerNode.getX() >= connectionx) {
                        this.drawLine(connectionx, connectiony, x + area.getX() + area.centerNode.getX() + SvgContainer.nodepadding, connectiony);
                    } else {
                        this.drawLine(connectionx, connectiony, x + area.getX() + area.centerNode.getX() + area.centerNode.getWidth() - SvgContainer.nodepadding, connectiony);
                    }
                }
                count++;
            })
        }

        if (this.childAreas.length == 1) {
            // draw connecting line
            let exactx = x + this.centerNode.getX() + this.centerNode.getWidth() / 2;
            let exacty1 = y + this.centerNode.getY() + this.centerNode.getHeight();
            let area = this.childAreas[0];
            let exacty2 = y + area.getY() + area.centerNode.getY() + SvgContainer.nodepadding;
            this.drawLine(exactx, exacty1, exactx, exacty2);
        } else if (this.childAreas.length > 1) {
            let exactx = x + this.centerNode.getX() + this.centerNode.getWidth() / 2;
            let exacty1 = y + this.centerNode.getY() + this.centerNode.getHeight();
            // first part of line to crosspoint
            //                DrawableArea area = parentAreas.get(0);
            let exacty2 = exacty1 + SvgContainer.layerpadding;
            this.drawLine(exactx, exacty1, exactx, exacty2);

            let orphans: DrawableArea[] = [];
            let families: DrawableArea[] = [];

            this.childAreas.forEach(area => {
                if (this.hasChildren(area)) {
                    families.push(area);
                } else {
                    orphans.push(area);
                }
            })

            // for each to the family areas we have to draw to lines from the crossing point
            // also for the first layer of orphans
            families.forEach(area => {
                // draw to lines one from the connection point to the center of the area centernode
                // and the other one from that point to the
                let familyx = x + area.getX() + area.centerNode.getX() + (area.centerNode.getWidth() / 2);
                let familyy = y + area.getY() + area.centerNode.getY() + SvgContainer.nodepadding;
                this.drawLine(exactx, exacty2, familyx, exacty2);
                this.drawLine(familyx, exacty2, familyx, familyy);
            })

            let count = 0, row = 0;
            let connectionx = exactx, connectiony = exacty2;

            orphans.forEach(area => {
                if ((count % 2) == 0) {
                    row++;
                    // draw line from connection point to new connection point for each new row
                    if (row > 1) {
                        let newcy = y + area.getY() + area.centerNode.getY() + (area.centerNode.getHeight() / 2);
                        this.drawLine(connectionx, connectiony, connectionx, newcy);
                        connectiony = newcy;
                    }
                }

                // draw line for first row similar to families
                if (row == 1) {
                    let familyx = x + area.getX() + area.centerNode.getX() + (area.centerNode.getWidth() / 2);
                    let familyy = y + area.getY() + area.centerNode.getY() + SvgContainer.nodepadding;
                    this.drawLine(exactx, exacty2, familyx, exacty2);
                    this.drawLine(familyx, exacty2, familyx, familyy);
                } else {
                    // draw line from new connection point to area
                    if (x + area.getX() + area.centerNode.getX() >= connectionx) {
                        this.drawLine(connectionx, connectiony, x + area.getX() + area.centerNode.getX() + SvgContainer.nodepadding, connectiony);
                    } else {
                        this.drawLine(connectionx, connectiony, x + area.getX() + area.centerNode.getX() + area.centerNode.getWidth() - SvgContainer.nodepadding, connectiony);
                    }
                }
                count++;
            });

        }
    }

    private drawLine(startx: number, starty: number, endx: number, endy: number) {
        SvgContainer.svg.append("line")
            .attr('x1', startx)
            .attr('y1', starty)
            .attr('x2', endx)
            .attr('y2', endy)
            .attr("stroke-width", 2)
            .attr("stroke", "#7f888f");
    }

    setX(x: number) {
        this.x = x;
    }

    getX(): number {
        return this.x;
    }

    setY(y: number) {
        this.y = y;
    }

    getY(): number {
        return this.y;
    }

    setWidth(width: number) {
        this.width = width;
    }

    getWidth(): number {
        return this.width;
    }

    setHeight(height: number) {
        this.height = height;
    }

    getHeight(): number {
        return this.height;
    }

    setPosition(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

}

/*
To encapsulate the dimensions for a single node.
*/

class NodeLayout {

    private x: number = 0;
    private y: number = 0;
    private width: number;
    private height: number;
    private node: RelationshipDiagram;

    private constructor(node: RelationshipDiagram) {
        this.node = node;
        this.width = 220 + (2 * SvgContainer.nodepadding)
        let metadataSpace = 0;
        if (node.metadata.size > 1) {
            metadataSpace = node.metadata.size - 1;
        }
        this.height = 45 + (2 * SvgContainer.nodepadding) + (10 * metadataSpace);
    }

    public static createNodeLayout(nodeVO: RelationshipDiagram): NodeLayout {
        return new NodeLayout(nodeVO)
    }

    setX(x: number) {
        this.x = x;
    }

    getX(): number {
        return this.x;
    }

    setY(y: number) {
        this.y = y;
    }

    getY(): number {
        return this.y;
    }

    setWidth(width: number) {
        this.width = width;
    }

    getWidth(): number {
        return this.width;
    }

    setHeight(height: number) {
        this.height = height;
    }

    getHeight(): number {
        return this.height;
    }

    setPosition(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

    getNode(): RelationshipDiagram {
        return this.node;
    }

    draw(x: number, y: number) {
        let exactx = x + this.x;
        let exacty = y + this.y;

        SvgContainer.svg.append('rect')
            .attr('x', exactx + SvgContainer.nodepadding)
            .attr('y', exacty + SvgContainer.nodepadding)
            .attr('width', 220)
            .attr('height', this.height - (SvgContainer.nodepadding))
            .style('stroke', '#7f888f')
            .attr('fill', '#ffffff');

        let dataXStart = exactx;
        let dataYStart = exacty + SvgContainer.nodepadding / 2;
        let imageUrl = this.node.entityType.toUpperCase() === 'ORGANISATION' ? require('../../../../../assets/images/org_profile.png') : require("../../../../../assets/images/person.png");

        SvgContainer.svg.append('svg:image')
            .attr('height', 40)
            .attr('width', 40)
            .attr('x', dataXStart + 10)
            .attr('y', dataYStart + 10)
            .attr('xlink:href', imageUrl)
            .style('margin', '5px')

        SvgContainer.svg.append('text')
            .attr('x', dataXStart + SvgContainer.nodepadding + (SvgContainer.nodepadding * 5))
            .attr('y', dataYStart + SvgContainer.nodepadding * 2 + 5)
            .style('fill', '#7f888f')
            .style('font-size', '13px')
            .text(this.node.title)

        let text = '';
        let metaData = this.node.metadata;
        Object.getOwnPropertyNames(metaData).forEach(
            function (val, idx, array) {
                text = text + '' + val + ' : ' + metaData[val];
            }
        );

        SvgContainer.svg.append('text')
            .attr('x', dataXStart + SvgContainer.nodepadding + (SvgContainer.nodepadding * 5))
            .attr('y', dataYStart + SvgContainer.nodepadding * 2 + (SvgContainer.nodepadding * 2 + 2))
            .style('fill', '#9fa3a6')
            .style('font-size', '11px')
            .text(text)
    }

}


// WEBPACK FOOTER //
// ./src/app/entities/relations/relationship/relationship-diagram/structure-diagram-layout-manager.ts